import { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { Formik, Field, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import moment from 'moment';
import Flatpickr from 'react-flatpickr';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import {
  tConvert,
  convertTimeToMinutes,
  isTimeInShiftRange,
} from '../../helpers';
import WeekDayCheckbox from './WeekDayCheckbox';
import { DAYS_LIST } from '../../constants';
import Avatar from '../Avatar';
import TimeSlotSelect from '../common/TimeSlotSelect';

const CreateIndividualSchedule = ({
  exitingShifts,
  showCalendarAddShift,
  setShowCalendarAddShift,
  dateInfo,
  handleCreateSchedule,
  handleShiftsDisplay,
  breaksList,
  setSelectedEvent,
  typesList,
}) => {
  const permissions = useSelector((state) => state.permissionsReducer);
  const createIndividualScheduleFormRef = useRef(null);
  const typeSelectRef = useRef(null);
  const shiftSelectRef = useRef(null);

  useEffect(() => {
    if (showCalendarAddShift && createIndividualScheduleFormRef.current) {
      createIndividualScheduleFormRef.current.resetForm();
    }
  }, [showCalendarAddShift]);

  const datePickerOptions = {
    allowInvalidPreload: true,
    // minDate: 'today',
    dateFormat: 'M j, Y',
    monthSelectorType: 'static',
  };

  const scheduleTypes =
    typesList && typesList.length > 0
      ? typesList.map((type) => ({
          value: type._id,
          label: `${type.name} ${type.shortCode ? `(${type.shortCode})` : ''}`,
          type: type.type,
        }))
      : [];

  const shitOptions =
    exitingShifts && exitingShifts.length > 0
      ? exitingShifts.map((eShift) => {
          const sOption = {
            value: eShift.shiftId,
            label: eShift.name,
            shiftStart: eShift.shiftStart,
            shiftEnd: eShift.shiftEnd,
          };
          return sOption;
        })
      : [];

  const shiftTypes = [
    ...shitOptions,
    { value: 'add', label: 'ADD NEW SHIFT NAME', isButton: true },
  ];
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');

  const { resource, date: scheduleDate, dateStr } = dateInfo;

  const breakOptions =
    breaksList && breaksList.length > 0
      ? breaksList.map((breakItem) => {
          const bOption = {
            value: breakItem._id,
            label: `${breakItem.name} ${
              breakItem.shortCode ? `(${breakItem.shortCode})` : ''
            }`,
          };
          return bOption;
        })
      : [];

  const scheduleValidationSchema = (shiftFrom, shiftTo) =>
    Yup.object({
      scheduleType:
        typesList && typesList.length > 0
          ? Yup.mixed().required('Please select schedule type')
          : Yup.mixed().nullable(),
      shiftId: Yup.string().when('scheduleType', {
        is: (scheduleType) =>
          !scheduleType ||
          !['leave', 'holiday', 'timeOff', 'weekOff'].includes(
            scheduleType.type
          ),
        then: () => Yup.string().required('Please select a shift'),
        otherwise: () => Yup.string(),
      }),
      shiftFrom: Yup.string().required('Please select start time'),
      shiftTo: Yup.string().required('Please select end time'),
      fromDate: Yup.date().required('Please select shift start date'),
      toDate: Yup.date()
        .required('Please select shift end date')
        .min(
          Yup.ref('fromDate'),
          'Shift end date must be equal or greater start date'
        ),
      shiftDays: Yup.array().required().min(1, 'Please select shift days'),
      breaks: Yup.array().of(
        Yup.object().shape({
          breakType: Yup.object().required('Break end time is required'),
          breakStart: Yup.object()
            .required('Break start time is required')
            .test(
              'is-within-shift',
              'Break start time must be within shift hours',
              function (breakStart) {
                if (!breakStart || !breakStart.value) return true;

                const startTime = shiftFrom || '09:00';
                const endTime = shiftTo || '17:00';

                const shiftStartMinutes = convertTimeToMinutes(startTime);
                const shiftEndMinutes = convertTimeToMinutes(endTime);
                const breakStartMinutes = convertTimeToMinutes(breakStart);

                return isTimeInShiftRange(
                  breakStartMinutes,
                  shiftStartMinutes,
                  shiftEndMinutes
                );
              }
            ),
          breakEnd: Yup.object()
            .required('Break end time is required')
            .test(
              'is-within-shift',
              'Break end time must be within shift hours',
              function (breakEnd) {
                if (!breakEnd || !breakEnd.value) return true;

                const startTime = shiftFrom || '09:00';
                const endTime = shiftTo || '17:00';

                const shiftStartMinutes = convertTimeToMinutes(startTime);
                const shiftEndMinutes = convertTimeToMinutes(endTime);
                const breakEndMinutes = convertTimeToMinutes(breakEnd);

                return isTimeInShiftRange(
                  breakEndMinutes,
                  shiftStartMinutes,
                  shiftEndMinutes
                );
              }
            )
            .test(
              'end-after-start',
              'Break end time must be after break start time',
              function (breakEnd) {
                // eslint-disable-next-line react/no-this-in-sfc
                const { breakStart } = this.parent;

                if (!breakStart?.value || !breakEnd?.value) return true;

                const start = convertTimeToMinutes(breakStart);
                let end = convertTimeToMinutes(breakEnd);

                if (end < start) {
                  end += 1440; // assume break ends next day
                }

                return end > start;
              }
            ),
        })
      ),
    });

  return (
    <>
      <Modal
        show={showCalendarAddShift}
        onHide={() => {
          setShowCalendarAddShift(false);
          setSelectedEvent({});
        }}
        centered
      >
        <Modal.Header>
          <Modal.Title className="m-0">Create Schedule</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => {
              setShowCalendarAddShift(false);
            }}
          />
        </Modal.Header>
        {resource && resource.extendedProps && (
          <Formik
            innerRef={createIndividualScheduleFormRef}
            enableReinitialize
            initialValues={{
              selectedMembers: [resource.id],
              scheduleType: '',
              shiftId: '',
              shiftFrom: '09:00',
              shiftTo: '17:00',
              fromDate: moment(dateStr),
              toDate: moment(dateStr),
              shiftDays: [
                moment.utc(scheduleDate).format('dddd').toLowerCase(),
              ],
              timezone: 'account',
              breaks: [],
            }}
            validationSchema={scheduleValidationSchema(fromTime, toTime)}
          >
            {(formikprops) => (
              <Form onSubmit={formikprops.handleSubmit}>
                <Modal.Body>
                  <section>
                    <Form.Group>
                      <Row className="align-items-center">
                        <Col className="col-auto">
                          <div className="avatar avatar-md">
                            <Avatar
                              firstName={resource.extendedProps.firstName}
                              lastName={resource.extendedProps.lastName}
                              defaultBg={resource.extendedProps.bgColor}
                              imgPath={resource.extendedProps.avatar}
                            />
                          </div>
                        </Col>
                        <Col className="ps-0">
                          <h4 className="mb-0">{resource.title}</h4>
                        </Col>
                      </Row>
                    </Form.Group>
                    {typesList && typesList.length > 0 && (
                      <Form.Group>
                        <Form.Label>Schedule Type</Form.Label>
                        <Select
                          ref={typeSelectRef}
                          options={scheduleTypes}
                          className="basic-multi-select"
                          placeholder="Select type"
                          name="scheduleType"
                          onChange={(event) => {
                            formikprops.setFieldValue('scheduleType', event);
                            if (
                              [
                                'leave',
                                'holiday',
                                'timeOff',
                                'weekOff',
                              ].includes(event.type)
                            ) {
                              formikprops.setFieldValue('shiftId', '');
                            }
                          }}
                          theme={(theme) => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              primary: '#0649b5',
                              neutral10: '#edf2f9',
                              neutral20: '#d2ddec',
                              neutral30: '#d2ddec',
                              neutral50: '#b1c2da',
                              neutral80: '#12263f',
                              dangerLight: '#DEEBFF',
                              danger: '#12263f',
                            },
                          })}
                          styles={{
                            option: (provided, state) => ({
                              ...provided,
                              color: state.data.isButton
                                ? '#0649b5'
                                : 'inherit',
                              fontWeight: state.data.isButton
                                ? 'bold'
                                : 'normal',
                              fontSize: state.data.isButton
                                ? '12px'
                                : 'inherit',
                            }),
                          }}
                        />
                        <ErrorMessage
                          name="scheduleType"
                          render={(msg) => (
                            <small className="text-danger">{msg}</small>
                          )}
                        />
                      </Form.Group>
                    )}

                    {(!formikprops.values.scheduleType ||
                      !['leave', 'holiday', 'timeOff', 'weekOff'].includes(
                        formikprops.values.scheduleType.type
                      )) && (
                      <Form.Group>
                        <Form.Label>Shift Slot</Form.Label>
                        <Select
                          ref={shiftSelectRef}
                          options={shiftTypes}
                          className="basic-multi-select"
                          placeholder="Select shift"
                          name="shiftId"
                          onChange={(event) => {
                            if (event.value === 'add') {
                              handleShiftsDisplay(true);
                              shiftSelectRef.current.select.clearValue();
                            } else {
                              formikprops.setFieldValue('shiftId', event.value);
                              formikprops.setFieldValue(
                                'shiftFrom',
                                event.shiftStart
                              );
                              formikprops.setFieldValue(
                                'shiftTo',
                                event.shiftEnd
                              );
                              setFromTime({
                                value: event.shiftStart,
                                label: tConvert(event.shiftStart),
                              });
                              setToTime({
                                value: event.shiftEnd,
                                label: tConvert(event.shiftEnd),
                              });
                            }
                          }}
                          theme={(theme) => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              primary: '#0649b5',
                              neutral10: '#edf2f9',
                              neutral20: '#d2ddec',
                              neutral30: '#d2ddec',
                              neutral50: '#b1c2da',
                              neutral80: '#12263f',
                              dangerLight: '#DEEBFF',
                              danger: '#12263f',
                            },
                          })}
                          styles={{
                            option: (provided, state) => ({
                              ...provided,
                              color: state.data.isButton
                                ? '#0649b5'
                                : 'inherit',
                              fontWeight: state.data.isButton
                                ? 'bold'
                                : 'normal',
                              fontSize: state.data.isButton
                                ? '12px'
                                : 'inherit',
                            }),
                          }}
                        />
                        <ErrorMessage
                          name="shiftId"
                          render={(msg) => (
                            <small className="text-danger">{msg}</small>
                          )}
                        />
                      </Form.Group>
                    )}

                    {(!formikprops.values.scheduleType ||
                      !['leave', 'holiday', 'timeOff', 'weekOff'].includes(
                        formikprops.values.scheduleType.type
                      )) &&
                      formikprops.values.shiftId && (
                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Shift Duration From</Form.Label>
                              <div>
                                {tConvert(formikprops.values.shiftFrom)}
                              </div>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Shift Duration To</Form.Label>
                              <div>{tConvert(formikprops.values.shiftTo)}</div>
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                    <Row>
                      <Col>
                        <Form.Group controlId="timezone">
                          <Form.Label>Timezone</Form.Label>
                          <Field
                            className="form-select"
                            as="select"
                            name="timezone"
                          >
                            <option value="account">Account Timezone </option>
                            <option value="user">Team member Timezone</option>
                          </Field>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group controlId="fromDate">
                          <Form.Label>Start Date</Form.Label>
                          <Flatpickr
                            onChange={([date]) => {
                              formikprops.setFieldValue('fromDate', date);
                            }}
                            placeholder=""
                            options={{
                              ...datePickerOptions,
                              defaultDate: moment(dateStr).toISOString(),
                            }}
                            className="form-control icon-calendar due-date"
                            name="fromDate"
                          />
                          <ErrorMessage
                            name="fromDate"
                            render={(msg) => (
                              <small className="text-danger">{msg}</small>
                            )}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="toDate">
                          <Form.Label>End Date</Form.Label>
                          <Flatpickr
                            onChange={([date]) => {
                              formikprops.setFieldValue('toDate', date);
                            }}
                            placeholder=""
                            options={{
                              ...datePickerOptions,
                              defaultDate: moment(dateStr).toISOString(),
                            }}
                            className="form-control icon-calendar due-date"
                            name="toDate"
                          />
                          <ErrorMessage
                            name="toDate"
                            render={(msg) => (
                              <small className="text-danger">{msg}</small>
                            )}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    {formikprops.values.scheduleType &&
                      ['leave', 'holiday', 'timeOff', 'weekOff'].includes(
                        formikprops.values.scheduleType.type
                      ) && (
                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Duration From</Form.Label>
                              <TimeSlotSelect
                                placeholder="09:00 AM"
                                name="shiftFrom"
                                value={fromTime}
                                onChange={(event) => {
                                  formikprops.setFieldValue(
                                    'shiftFrom',
                                    event.value
                                  );
                                  setFromTime({
                                    value: event.value,
                                    label: event.label,
                                  });
                                }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Duration To</Form.Label>
                              <TimeSlotSelect
                                placeholder="05:00 PM"
                                name="shiftTo"
                                value={toTime}
                                onChange={(event) => {
                                  formikprops.setFieldValue(
                                    'shiftTo',
                                    event.value
                                  );
                                  setToTime({
                                    value: event.value,
                                    label: event.label,
                                  });
                                }}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>Select Days</Form.Label>
                          <div className="days-selection">
                            {DAYS_LIST.map((day) => (
                              <WeekDayCheckbox
                                dayName={day}
                                key={day}
                                formikprops={formikprops}
                              />
                            ))}
                          </div>
                          <ErrorMessage
                            name="shiftDays"
                            render={(msg) => (
                              <small className="text-danger">{msg}</small>
                            )}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    {(!formikprops.values.scheduleType ||
                      !['leave', 'holiday', 'timeOff', 'weekOff'].includes(
                        formikprops.values.scheduleType.type
                      )) && (
                      <>
                        {breaksList && breaksList.length > 0 && (
                          <FieldArray
                            name="breaks"
                            render={(arrayHelpers) => (
                              <div>
                                {formikprops.values.breaks &&
                                  formikprops.values.breaks.length > 0 &&
                                  formikprops.values.breaks.map(
                                    (breakItem, bIndex) => (
                                      <Row key={`breaks${bIndex}`}>
                                        <Col xs={5}>
                                          <Form.Group>
                                            <Select
                                              options={breakOptions}
                                              className="basic-multi-select"
                                              placeholder="Select Break Type"
                                              name={`breaks[${bIndex}].breakType`}
                                              onChange={(event) =>
                                                formikprops.setFieldValue(
                                                  `breaks[${bIndex}].breakType`,
                                                  event
                                                )
                                              }
                                              value={
                                                formikprops.values.breaks[
                                                  bIndex
                                                ].breakType.value
                                                  ? {
                                                      label:
                                                        formikprops.values
                                                          .breaks[bIndex]
                                                          .breakType.label,
                                                      value:
                                                        formikprops.values
                                                          .breaks[bIndex]
                                                          .breakType.value,
                                                    }
                                                  : null
                                              }
                                              theme={(theme) => ({
                                                ...theme,
                                                colors: {
                                                  ...theme.colors,
                                                  primary: '#0649b5',
                                                  neutral10: '#edf2f9',
                                                  neutral20: '#d2ddec',
                                                  neutral30: '#d2ddec',
                                                  neutral50: '#b1c2da',
                                                  neutral80: '#12263f',
                                                  dangerLight: '#DEEBFF',
                                                  danger: '#12263f',
                                                },
                                              })}
                                            />
                                            <ErrorMessage
                                              name={`breaks[${bIndex}].breakType`}
                                              render={(msg) => (
                                                <small className="text-danger">
                                                  {msg}
                                                </small>
                                              )}
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col xs={3}>
                                          <TimeSlotSelect
                                            name={`breaks[${bIndex}].breakStart`}
                                            placeholder="From"
                                            onChange={(event) =>
                                              formikprops.setFieldValue(
                                                `breaks[${bIndex}].breakStart`,
                                                event
                                              )
                                            }
                                            value={
                                              formikprops.values.breaks[bIndex]
                                                .breakStart.value
                                                ? {
                                                    label:
                                                      formikprops.values.breaks[
                                                        bIndex
                                                      ].breakStart.label,
                                                    value:
                                                      formikprops.values.breaks[
                                                        bIndex
                                                      ].breakStart.value,
                                                  }
                                                : null
                                            }
                                          />
                                          <ErrorMessage
                                            name={`breaks[${bIndex}].breakStart`}
                                            render={(msg) => (
                                              <small className="text-danger">
                                                {msg}
                                              </small>
                                            )}
                                          />
                                        </Col>
                                        <Col xs={3}>
                                          <TimeSlotSelect
                                            name={`breaks[${bIndex}].breakEnd`}
                                            placeholder="To"
                                            onChange={(event) =>
                                              formikprops.setFieldValue(
                                                `breaks[${bIndex}].breakEnd`,
                                                event
                                              )
                                            }
                                            value={
                                              formikprops.values.breaks[bIndex]
                                                .breakEnd.value
                                                ? {
                                                    label:
                                                      formikprops.values.breaks[
                                                        bIndex
                                                      ].breakEnd.label,
                                                    value:
                                                      formikprops.values.breaks[
                                                        bIndex
                                                      ].breakEnd.value,
                                                  }
                                                : null
                                            }
                                          />
                                          <ErrorMessage
                                            name={`breaks[${bIndex}].breakEnd`}
                                            render={(msg) => (
                                              <small className="text-danger">
                                                {msg}
                                              </small>
                                            )}
                                          />
                                        </Col>
                                        <Col xs={1}>
                                          <Button
                                            variant="link"
                                            onClick={() =>
                                              arrayHelpers.remove(bIndex)
                                            }
                                          >
                                            <i className="mdi mdi-window-close" />
                                          </Button>
                                        </Col>
                                      </Row>
                                    )
                                  )}
                                <div className="mb-4">
                                  <Button
                                    variant="outline-primary"
                                    type="button"
                                    size="sm"
                                    onClick={() =>
                                      arrayHelpers.push({
                                        breakType: '',
                                        breakStart: '',
                                        breakEnd: '',
                                      })
                                    }
                                  >
                                    Add Break
                                  </Button>
                                </div>
                              </div>
                            )}
                          />
                        )}
                      </>
                    )}
                  </section>
                  {formikprops.values.scheduleType?.type === 'overtime' && (
                    <Alert variant="light" className="mb-0">
                      <strong>Note:</strong> OT shifts require approval. They’ll
                      be published automatically once accepted by the assigned
                      user.
                    </Alert>
                  )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                  <Button
                    variant="link"
                    onClick={() => {
                      setShowCalendarAddShift(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <span>
                    <Button
                      variant="outline-primary"
                      type="submit"
                      className="me-2"
                      onClick={() => {
                        formikprops.validateForm().then((errors) => {
                          if (Object.keys(errors).length === 0) {
                            handleCreateSchedule({
                              ...formikprops.values,
                              isPublished: false,
                            });
                          }
                        });
                      }}
                    >
                      Save as Draft
                    </Button>
                    {permissions &&
                      permissions.includes('schedule_publish') && (
                        <Button
                          variant="primary"
                          type="submit"
                          onClick={() => {
                            formikprops.validateForm().then((errors) => {
                              if (Object.keys(errors).length === 0) {
                                handleCreateSchedule({
                                  ...formikprops.values,
                                  isPublished: true,
                                });
                              }
                            });
                          }}
                          disabled={
                            formikprops.values.scheduleType?.type === 'overtime'
                          }
                        >
                          Publish
                        </Button>
                      )}
                  </span>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </>
  );
};

CreateIndividualSchedule.propTypes = {
  exitingShifts: PropTypes.array,
  showCalendarAddShift: PropTypes.bool,
  setShowCalendarAddShift: PropTypes.func,
  dateInfo: PropTypes.object,
  handleCreateSchedule: PropTypes.func,
  handleShiftsDisplay: PropTypes.func,
  breaksList: PropTypes.array,
  setSelectedEvent: PropTypes.func,
  typesList: PropTypes.array,
};

export default CreateIndividualSchedule;
